/*
 **	Animated Stat Card Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React from 'react'
import { OCAColors, Typography, Spacing, Animation } from '../../theme/colors'

interface StatCardProps {
  value: number
  label: string
  color: string
  icon?: string
  trend?: 'up' | 'down'
}

export const StatCard: React.FC<StatCardProps> = ({ value, label, color, icon, trend }) => {
  const [isPressed, setIsPressed] = React.useState(false)

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
        borderRadius: '16px',
        padding: Spacing.lg,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
        border: `1px solid rgba(0, 0, 0, 0.06)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: Spacing.sm,
        minHeight: '110px',
        position: 'relative',
        overflow: 'hidden',
        transition: `all ${Animation.normal} cubic-bezier(0.4, 0, 0.2, 1)`,
        transform: isPressed ? 'scale(0.96)' : 'scale(1)',
        cursor: 'pointer',
      }}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {/* Colored accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${color}, ${color}dd)`,
          borderRadius: '16px 16px 0 0',
        }}
      />

      {/* Icon badge */}
      {icon && (
        <div
          style={{
            position: 'absolute',
            top: Spacing.sm,
            right: Spacing.sm,
            fontSize: '18px',
            opacity: 0.3,
          }}
        >
          {icon}
        </div>
      )}

      {/* Value with trend indicator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <div
          style={{
            fontSize: Typography.fontSize['3xl'],
            fontWeight: Typography.fontWeight.bold,
            color,
            lineHeight: '1',
            animation: 'countUp 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          }}
        >
          {value}
        </div>
        {trend && (
          <div
            style={{
              fontSize: '16px',
              animation: 'bounce 2s ease-in-out infinite',
            }}
          >
            {trend === 'up' ? '↑' : '↓'}
          </div>
        )}
      </div>

      {/* Label */}
      <div
        style={{
          fontSize: Typography.fontSize.xs,
          fontWeight: Typography.fontWeight.medium,
          color: OCAColors.secondaryLabel,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          textAlign: 'center',
        }}
      >
        {label}
      </div>

      {/* Subtle background pattern */}
      <div
        style={{
          position: 'absolute',
          bottom: '-20px',
          right: '-20px',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}15, transparent)`,
          pointerEvents: 'none',
        }}
      />

      {/* Animations */}
      <style>{`
        @keyframes countUp {
          0% {
            opacity: 0;
            transform: translateY(10px) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
      `}</style>
    </div>
  )
}
